import { TwoFactorResponse } from "@sp-crm/core";
import {
    TwoFactorCodeInput,
    TwoFactorMethodSelector,
    useTwoFactorFlow,
} from "components/shared/two-factor";
import { InlineBanner } from "components/ui/inline-banner";
import { TwoFactorMethod } from "generated/graphql";
import React, { FormEvent, useCallback, useState } from "react";
import { reportError } from "util/error-tracking";
import { handleEvent } from "util/user-events";
import * as http from "../../util/http";

interface PasswordResetVerifyCodeResponse {
    success: boolean;
    err?: string;
    msg?: string;
}

interface PasswordReset2FAInlineProps {
    twoFactorData: TwoFactorResponse;
    onComplete: () => void;
    errorMessage?: string | null;
    token: string;
}

export const PasswordReset2FAInline: React.FC<PasswordReset2FAInlineProps> = ({
    twoFactorData,
    onComplete,
    errorMessage,
    token,
}) => {
    const [error, setError] = useState<string | null>(errorMessage || null);

    const {
        verificationCode,
        setVerificationCode,
        selectedMethod,
        codeSent,
        resendCountdown,
        loading,
        setLoading,
        sendCode,
        handleResendCode,
    } = useTwoFactorFlow({
        twoFactorData,
        sendCodeEndpoint: "/api/password/send-verification-code",
        onSendCodeError: () => {},
        eventPrefix: "2fa-password-reset",
    });

    const handleMethodSelection = (method: TwoFactorMethod): void => {
        sendCode(method);
    };

    const handleVerificationSubmit = useCallback(
        async (e: FormEvent<HTMLFormElement>): Promise<void> => {
            e.preventDefault();
            if (!twoFactorData || !verificationCode) return;

            try {
                setLoading(true);
                const response = await http.post<PasswordResetVerifyCodeResponse>(
                    http.newRequestDescription("/api/password/verify-code"),
                    "/api/password/verify-code",
                    {
                        code: verificationCode,
                    },
                );

                if (response.data.success) {
                    setError(null);
                    handleEvent("2fa-password-reset-success", { method: selectedMethod });
                    onComplete();
                } else {
                    const errorMessage =
                        response.data.err || response.data.msg || "Verification failed";
                    setError(errorMessage);
                    handleEvent("2fa-password-reset-fail", {
                        method: selectedMethod,
                        step: "verify-code",
                        error: errorMessage,
                    });
                }
            } catch (error) {
                const errorMessage =
                    (
                        error as {
                            response?: { data?: { err?: string; msg?: string } };
                            message?: string;
                        }
                    )?.response?.data?.msg ||
                    (
                        error as {
                            response?: { data?: { err?: string; msg?: string } };
                            message?: string;
                        }
                    )?.response?.data?.err ||
                    (error as Error)?.message ||
                    "An error occurred. Please try again.";
                setError(errorMessage);
                handleEvent("2fa-password-reset-fail", {
                    method: selectedMethod,
                    step: "verify-code",
                });
                reportError(error as Error, {
                    fingerprint: "post:/api/password/verify-code",
                    params: { step: "verify-code" },
                    context: { method: selectedMethod },
                });
            } finally {
                setLoading(false);
            }
        },
        [selectedMethod, twoFactorData, verificationCode, onComplete, setLoading],
    );

    if (!twoFactorData) {
        return null;
    }

    return (
        <div className="max-w-md mx-auto">
            <h3 className="text-lg font-semibold mb-4">
                Two-Factor Authentication Required
            </h3>
            <p className="text-gray-600 mb-6">
                Please complete two-factor authentication to reset your password.
            </p>

            {error && (
                <div className="mb-4">
                    <InlineBanner type="error">{error}</InlineBanner>
                </div>
            )}

            {!codeSent && twoFactorData?.methods && twoFactorData.methods.length > 1 && (
                <div className="max-w-sm">
                    <TwoFactorMethodSelector
                        methods={twoFactorData.methods}
                        onMethodSelect={handleMethodSelection}
                        loading={loading}
                    />
                </div>
            )}

            {codeSent && (
                <TwoFactorCodeInput
                    verificationCode={verificationCode}
                    onCodeChange={e => setVerificationCode(e.target.value)}
                    onSubmit={handleVerificationSubmit}
                    onResend={handleResendCode}
                    selectedMethod={selectedMethod}
                    resendCountdown={resendCountdown}
                    loading={loading}
                    contextText="We've sent a verification code to your phone or email. Please enter it below to continue."
                />
            )}
        </div>
    );
};
